/** @jsx jsx */
import { jsx, css, CSSObject } from '@emotion/react';
import { List, AutoSizer, InfiniteLoader, CellMeasurerCache, IndexRange, CellMeasurer, ListRowRenderer } from 'react-virtualized';
import { Classes } from '@blueprintjs/core';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { selectPropertyListFromDilByNames, selectDilDefinitionByName } from 'srs-middleware';
import { useFetchValues } from '/hooks/useFetchValues';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { ScrollBox } from 'components/scrollBox';
import { HeaderMd } from 'styles/styledComponentUtils';
import { Sizes } from 'styles/variables-slykit';
import { debounce } from 'lodash';

interface Props extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	rowRenderer: React.FunctionComponent<{
		item: any,
		index: number,
		updateIndex: (index: number, value: any) => void // IDK if this is right?
	}>,
	rowPlaceholderRenderer?: React.FunctionComponent
	minRowHeight?: number
	querySize?: number,
	properties?: string[],
	sortProperty?: string,
	sortDescending?: boolean,
}

export const InfiniteScrollingList: React.FunctionComponent<Props> = ({
	rowRenderer: Row,
	rowPlaceholderRenderer: RowPlaceholder = DefaultRowPlaceholder,
	properties, // = [], // ??
	minRowHeight = 80,
	querySize = 30,
	sortProperty,
	sortDescending = false,
}) => {
	const cache = useRef(new CellMeasurerCache({ fixedWidth: true, defaultHeight: minRowHeight, minHeight: minRowHeight }));
	const [indexRange, setIndexRange] = useState<{ startIndex: number, endIndex: number }>();
	const [localData, setLocalData] = useState({});
	const listRef = useRef<List>();
	const listRefCallback = useCallback((list: List) => listRef.current = list, [listRef]);

	const {
		state: {
			explorer: { selectedDefinitionName },
			transient: { activeResult },
		},
	} = useContext(WorkspaceContext);

	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);

	// memoize the parameters, so we don't keep fetching on reRender
	const parameters = useMemo(() => {
		if (!indexRange) return null;

		return {
			sortProperty: sortProperty && selectPropertyListFromDilByNames(selectedDefinitionName, [sortProperty])[0],
			descending: sortDescending,
			...indexRange,
		};
	}, [indexRange, selectedDefinitionName, sortProperty, sortDescending]);

	const { data } = useFetchValues(selectedDefinition, activeResult, properties, parameters);
	

	// we cache the data so we know what we have
	// TODO: This should be a cache, and not keep everything forever
	useEffect(() => {
		if (!data) return;
		// these data checks help prevent loading in duplicate data.
		// indexRange updates before the data is fetched, and triggers it to 
		// try and load the list with the initial batch of data again.
		if (data.length > indexRange.endIndex + 1) {
			setIndexRange(range => ({
				...range,
				endIndex: data.length - 1
			}));
			return;
		}
		if (data.length - 1 > indexRange.endIndex - indexRange.startIndex) {
			return;
		}
		for (let i = 0; i < data.length; i++) {
			localData[indexRange.startIndex + i] = data[i];
			cache.current.clear(indexRange.startIndex + i, 0)
		}
		setLocalData(localData);
	}, [data, indexRange, localData, cache]);

	// recalculate rowHeights when the window resizes 
	const forceUpdate = useCallback(debounce(() => { // debounce is required or it won't work correctly
		cache.current.clearAll()
		listRef.current?.recomputeRowHeights()
	}, 300), [listRef, cache])
	useEffect(() => {
		window.addEventListener("resize", forceUpdate);
		return () => window.removeEventListener("resize", forceUpdate) // don't forget to unsubscribe!
	}, [forceUpdate])

	// Only load 1 page of items at a time.
	const loadMoreRows = ({ startIndex, stopIndex }: IndexRange) => {
		return new Promise((resolve) => {
			setIndexRange({ startIndex: startIndex, endIndex: stopIndex });
			resolve(true);
		});
	};

	// check the cache for the proper index
	const isRowLoaded = ({ index }) => {
		return localData[index] !== undefined;
	};

	// allows for updating a single index within the rowRenderer
	const updateIndex = useCallback((index, value) => {
		localData[index] = value;
		setLocalData(localData);
		cache.current.clear(index, 0)
	}, [localData, setLocalData, cache, listRef]);

	// render the proper row, or a loading placeholder
	const ListRow: ListRowRenderer = ({ index, key, style, parent }) => (
		<CellMeasurer cache={cache.current} key={key} parent={parent} index={index}>
			{({ registerChild }) => (
				<div css={style as CSSObject} ref={registerChild}>
					{(!isRowLoaded({ index }))
						? RowPlaceholder({})
						: Row({ item: localData[index], index, updateIndex })
					}
				</div>
			)}
		</CellMeasurer>
	);

	return (
		<ScrollBox>
			<InfiniteLoader
				loadMoreRows={loadMoreRows}
				isRowLoaded={isRowLoaded}
				rowCount={activeResult?.count || 1}
				minimumBatchSize={querySize}
			>
				{({ onRowsRendered, registerChild }) => (
					<AutoSizer>
						{({ width, height }) => (
							<List
								width={width}
								height={height}
								ref={(list) => {
									registerChild(list)
									listRefCallback(list)
								}}
								onRowsRendered={onRowsRendered}
								deferredMeasurementCache={cache.current}
								rowHeight={cache.current.rowHeight}
								rowCount={activeResult?.count || 0}
								rowRenderer={ListRow}
							/>
						)}
					</AutoSizer>
				)}
			</InfiniteLoader>
		</ScrollBox>
	);
};


interface RowProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	index: number
}

export const DefaultRowPlaceholder: React.FunctionComponent<RowProps> = ({ index, ...props }) => (
	<div css={css`padding: ${Sizes.md} ${Sizes.lg};`} {...props}>
		<HeaderMd className={Classes.SKELETON} css={css` display:inline-block; margin-bottom:0.5rem; `}>
			Loading Item
		</HeaderMd>
		<p className={Classes.SKELETON}>
			Lorem ipsum dolor sit amet, consectetur adipiscing elit,
			sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
			Etiam erat velit scelerisque in.
		</p>
	</div>
);
